import React from 'react'
import { Flex, Skeleton, Typography } from 'antd'
import dayjs from 'dayjs'
import { EmployeeCeremonyResponseItem } from '@/features/employee-data/employee-data.def.ts'
import { InitialsAvatar } from '@/common/components/InitialsAvatar.tsx'
import { useGetEmployeeCeremonyDataQuery } from '@/features/employee-data/employee-data.api.ts'
import { orderBy } from 'lodash-es'


type PersonCardProps = {
  data: EmployeeCeremonyResponseItem;
};

const PersonCard: React.FC<PersonCardProps> = ({ data }) => {
  return (
    <div>
      <Flex
        gap="small"
        align="center"
        className="p-3 border border-gray-200 rounded-lg bg-white hover:shadow-md transition-shadow"
      >
        <InitialsAvatar value={data.displayName} />
        <Flex vertical style={{ minWidth: 0, flex: 1 }}>
          <Typography.Text strong className="text-sm truncate block">
            {data.displayName}
          </Typography.Text>
          <Typography.Text
            className="text-xs truncate block"
            style={{ color: '#d97706' }}
          >
            {data.title}
          </Typography.Text>
          <Typography.Text className="text-xs text-gray-400 mt-0.5">
            {dayjs(data.refDate).format('MMM D, YYYY')}
          </Typography.Text>
        </Flex>
      </Flex>
    </div>
  )
}

const PersonCardSkeleton: React.FC = () => {
  return (
    <div className="p-1 border border-gray-200 rounded-lg bg-white">
      <Skeleton avatar active paragraph={{ rows: 2 }} title={false} />
    </div>
  )
}

type SectionProps = {
  icon: string;
  title: string;
  badge: string;
  items: EmployeeCeremonyResponseItem[];
  isFetching?: boolean;
};

const PeopleSection: React.FC<SectionProps> = ({
  icon,
  title,
  badge,
  items,
  isFetching,
}) => {
  return (
    <div className="rounded-xl p-4" style={{ background: '#f1f5f9' }}>
      <Flex align="center" gap="small" className="mb-4">
        <span style={{ fontSize: 28 }}>{icon}</span>
        <Typography.Title level={5} style={{ margin: 0 }}>
          {title} <Typography.Text strong>({items.length})</Typography.Text>
        </Typography.Title>
        <Typography.Text type="secondary" className="text-sm">
          {badge}
        </Typography.Text>
      </Flex>
      {/*{isFetching ? (*/}
      <div className="grid grid-cols-3 gap-2">
        {isFetching ? (
          Array.from({ length: 2 }).map((_, index) => (
            <PersonCardSkeleton key={index} />
          ))
        ) : null}
        {!isFetching && items.length === 0 ? (
          <Typography.Text type="secondary" className="text-sm">
            No one yet
          </Typography.Text>
        ) : null}
        {!isFetching && items.length > 0 ? (
          items.map((x) => (
            <PersonCard key={x.employeeId} data={x} />
          ))
        ) : null}
      </div>
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

const birthdayFilterSet = new Set(['PC0542'])

export const HomePeopleSection: React.FC = () => {
  const { data, isFetching } = useGetEmployeeCeremonyDataQuery()

  return (
    <Flex vertical gap="middle">
      <PeopleSection
        icon="🎉"
        title="Birthday Buddies"
        badge="Next 7 days"
        items={orderBy(data?.birthdays, x => x.refDate)
        .filter(x => !birthdayFilterSet.has(x.employeeId)) || []}
        isFetching={isFetching}
      />
      <PeopleSection
        icon="🎁"
        title="Work Anniversaries"
        badge="Next 7 days"
        items={orderBy(data?.workAnniversaries, x => x.refDate) || []}
        isFetching={isFetching}
      />
      <PeopleSection
        icon="👥"
        title="New Hires"
        badge="Last 7 days"
        items={orderBy(data?.newHires, x => x.refDate) || []}
        isFetching={isFetching}
      />
    </Flex>
  )
}
